/**
 * Encrypted offline store (Phase 16A, design §6.2).
 *
 * One database per tenant + user (`pelora-offline-{hash}`). Every store except `meta` holds only
 * AES-GCM envelopes, bound to their "store:key" slot. `meta` holds the wrapped DEK, salt, KDF
 * parameters and timestamps: nothing that identifies a patient.
 */
import { decryptJson, encryptJson, Envelope } from './crypto';
import type { KvBackend, OfflineStoreName } from './platform/kvBackend';

export type EncryptedStoreName = Exclude<OfflineStoreName, 'meta'>;

export class SecureStore {
  private dek: CryptoKey | null = null;

  constructor(private readonly backend: KvBackend) {}

  setKey(dek: CryptoKey): void { this.dek = dek; }
  clearKey(): void { this.dek = null; }
  get unlocked(): boolean { return this.dek !== null; }

  private key(): CryptoKey {
    if (!this.dek) throw new Error('Offline store is locked');
    return this.dek;
  }

  // meta: plaintext, never PHI
  getMeta<T>(name: string): Promise<T | undefined> { return this.backend.get<T>('meta', name); }
  putMeta(name: string, value: unknown): Promise<void> { return this.backend.put('meta', name, value); }
  deleteMeta(name: string): Promise<void> { return this.backend.delete('meta', name); }

  async put(store: EncryptedStoreName, key: string, value: unknown): Promise<void> {
    await this.backend.put(store, key, await encryptJson(this.key(), value, `${store}:${key}`));
  }

  async get<T>(store: EncryptedStoreName, key: string): Promise<T | undefined> {
    const envelope = await this.backend.get<Envelope>(store, key);
    return envelope ? decryptJson<T>(this.key(), envelope, `${store}:${key}`) : undefined;
  }

  async getAll<T>(store: EncryptedStoreName): Promise<Array<{ key: string; value: T }>> {
    const dek = this.key();
    const rows = await this.backend.getAll<Envelope>(store);
    return Promise.all(rows.map(async ({ key, value }) => ({ key, value: await decryptJson<T>(dek, value, `${store}:${key}`) })));
  }

  delete(store: EncryptedStoreName, key: string): Promise<void> { return this.backend.delete(store, key); }
  clear(store: OfflineStoreName): Promise<void> { return this.backend.clear(store); }

  /** True when the store has any record (readable while locked; values stay encrypted). */
  async hasRows(store: OfflineStoreName): Promise<boolean> {
    return (await this.countRows(store)) > 0;
  }

  /** Number of records (readable while locked: the pending-op count for the R-311 prompt). */
  async countRows(store: OfflineStoreName): Promise<number> {
    return (await this.backend.getAll(store)).length;
  }

  // escrow: already sealed to the tenant public key (16C, design §6.4). Stored without the DEK so
  // it survives a PIN shred and can be uploaded by the same user after a fresh sign-in.
  putSealed(key: string, value: unknown): Promise<void> { return this.backend.put('escrow', key, value); }
  getAllSealed<T>(): Promise<Array<{ key: string; value: T }>> { return this.backend.getAll<T>('escrow'); }
  deleteSealed(key: string): Promise<void> { return this.backend.delete('escrow', key); }

  async destroy(): Promise<void> {
    this.clearKey();
    await this.backend.destroy();
  }
}

export default SecureStore;
